import React, { useRef, useCallback, useMemo } from 'react';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import Button from '../../../../components/Button/CTA/index.js';
import CancelIcon from '../../../../components/Icon/Times.js';
import BoatPreview$1 from '../index.js';
import { withCouponContext } from '../../../../contexts/coupon.js';
import { withCruiseContext } from '../../../../contexts/boat/cruise.js';
import { useBoatFiltersContext } from '../../../../contexts/boat/filters.js';
import { useBoatListContext } from '../../../../contexts/boat/list.js';
import { useLocalizedUrl } from '../../../../hooks/locale/url.js';
import { useLazyEffect } from '../../../../hooks/effects/lazy.js';
import { useOpenClose } from '../../../../hooks/effects/toggle.js';
import { useDocumentScrollDisabled } from '../../../../hooks/effects/scroll.js';
import { resetGTM } from '../../../../hooks/tracking/gtm.js';
import { pushToDataLayer } from '../../../../utils/tracking/gtm/index.js';
import { boatEcommerceEvent, boatEvent } from '../../../../utils/tracking/gtm/events/boat.js';
import { applyFilterEvent, searchListEcommerceEvent, boatListEvent } from '../../../../utils/tracking/gtm/events/search.js';
import { hasTripDates, getDepartureSlug, getBoatModelSlug } from '../../../../utils/parsers/boat/listed.js';
import { logDebug } from '../../../../utils/debug/logger.js';
import styles from './styles.scss.js';

const BookingFlow = dynamic(() => import('../../../Booking/index.js'), { ssr: false });
const BoatPreviewModal = ({ onClose, value, ...props }) => {
    const router = useRouter();
    const localizedUrl = useLocalizedUrl();
    const referal = useRef({
        url: `/search${window.location.search}`,
        as: `${window.location.pathname}${window.location.search}`,
    });
    const { filters } = useBoatFiltersContext();
    const { boats } = useBoatListContext();
    const [showBooking, closeBooking, openBooking] = useOpenClose();
    const handleClose = useCallback(async () => {
        logDebug('BoatPreview: close');
        onClose();
        logDebug('GTM: Restore search page', referal.current);
        await resetGTM();
        pushToDataLayer(applyFilterEvent(filters));
        pushToDataLayer(searchListEcommerceEvent(boats, 'Search'));
        pushToDataLayer(boatListEvent(boats));
        window.history.replaceState(referal.current, null, referal.current.as);
    }, [onClose, filters, boats]);
    const url = useMemo(() => (hasTripDates(value) ? localizedUrl('/cruise/:id', { id: value.id })
        : localizedUrl('/boats/:marina/:slug/:id', { marina: getDepartureSlug(value), slug: getBoatModelSlug(value), id: value.boat.id })), []);
    const open = value != null;
    useDocumentScrollDisabled(open);
    useLazyEffect(async () => {
        logDebug('GTM: Simulate cruise page', value);
        await resetGTM();
        pushToDataLayer(boatEcommerceEvent(value, 'Cruise Page'));
        pushToDataLayer(boatEvent(value));
        logDebug('BoatPreview: push URL', value.id);
        window.history.pushState({
            url: `/cruise/[...slug]?slug=${value.id}`,
            as: `${router.locale}/cruise/${value.id}`,
        }, null, url);
    }, [], false);
    const handlePopState = useCallback(async () => {
        if (showBooking) {
            closeBooking();
            window.history.replaceState({
                url: `/cruise/[...slug]?slug=${value.id}`,
                as: `${router.locale}/cruise/${value.id}`,
            }, null, url);
        }
        else {
            logDebug('BoatPreview: pop state');
            onClose();
            logDebug('GTM: Back to search page', referal.current);
            await resetGTM();
            pushToDataLayer(applyFilterEvent(filters));
            pushToDataLayer(searchListEcommerceEvent(boats, 'Search'));
            pushToDataLayer(boatListEvent(boats));
            window.history.replaceState(referal.current, null, referal.current.as);
        }
    }, [onClose, filters, boats, showBooking]);
    useLazyEffect(() => {
        window.addEventListener('popstate', handlePopState);
        return () => {
            logDebug('BoatPreview: reset pop state');
            window.removeEventListener('popstate', handlePopState);
        };
    }, [handlePopState], false);
    return (React.createElement("div", { className: open ? styles.background : styles.closed },
        React.createElement("div", { className: styles.dialog },
            React.createElement(Button, { className: styles.close, onClick: handleClose, color: "neutral", inverted: true },
                React.createElement(CancelIcon, { width: 18, height: 18 })),
            showBooking ? React.createElement(BookingFlow, null)
                : React.createElement(BoatPreview$1, Object.assign({ onOpenBookingFlow: openBooking }, props)))));
};
var BoatPreview = withCouponContext(withCruiseContext(BoatPreviewModal));

export { BoatPreview as default };
//# sourceMappingURL=index.js.map
